import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faMapMarkerAlt, 
  faTicketAlt, 
  faSearch,
  faStar,
  faFilter,
  faChevronRight,
  faListUl,
  faCalendarDay,
  faCalendarPlus,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import LoadingSpinner from '../../common/LoadingSpinner';
import { processEvents, GET_EVENTS_QUERY, combineAndSortEvents, isPirateTakeoverByTitle } from '../../../utils/eventUtils';
import { useWhatsOnSection } from '../../../hooks/useWhatsOnSection';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
const eventsImage = 'https://BelvoirCastle.b-cdn.net/images/activities.webp';

// Import images
const retailImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/retail3.webp';

const christmasImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Christmas25.webp';
const imgCastleGrotto = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvoir%20Castle%20Christmas-94.webp';
const imgVillageGrotto = 'https://BelvoirCastle.b-cdn.net/Christmas/Santa%20Belvoir%20Retail%20Village%20-%20Image%202.webp';
const imgSearch = 'https://BelvoirCastle.b-cdn.net/Christmas/Designer.webp';
const imgMiceTrail = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvoir%20Castle%20Christmas-28.webp';
const imgAfternoonTea = 'https://BelvoirCastle.b-cdn.net/Afternoon%20Tea/Festive/WhatsApp%20Image%202026-10-20%20at%2010.13.47.jpeg';
const imgCandlelight = 'https://BelvoirCastle.b-cdn.net/Christmas/candle%20light/Belvoir%20Castle%20Christmas-59.webp';
const imgWreath = 'https://BelvoirCastle.b-cdn.net/Wellness/IMG_5574.webp';

const vineyardImage = 'https://BelvoirCastle.b-cdn.net/Banners/vineyard.webp';

const harvestHeroImage =
  'https://BelvoirCastle.b-cdn.net/Harvest/Harvest%20Day%20EY.webp';

const halloweenHeroImage =
  'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp';
const halloweenCraftsImage =
  'https://BelvoirCastle.b-cdn.net/Halloween/crafts.webp';

/* Bunny CDN — events page hero */
const EVENTS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/2.webp';

// Updated GraphQL query for featured events - using a simpler approach
const GET_FEATURED_EVENTS_QUERY = gql`
  query GetFeaturedEvents {
    events(where: {metaQuery: {metaArray: [{key: "event_is_featured", value: "1", compare: EQUAL_TO}]}}, first: 3) {
      nodes {
        id
        title
        date
        featuredImage {
          node {
            sourceUrl
          }
        }
        eventDetails {
          eventLocation
          eventDescription
          eventShortDescription
          eventDate
          eventEndDate
          eventTicketTier
          eventTags
          isFeatured
        }
      }
    }
  }
`;

// Styled components — Day Tickets / Home patterns (intro strip, section headers, cards)
const EventsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const EventsIntroSection = styled.section`
  padding: clamp(2rem, 4.5vw, 3rem) clamp(1.25rem, 4vw, 2rem)
    clamp(1.25rem, 3vw, 1.75rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  background: linear-gradient(
    180deg,
    ${props => props.theme.colors.offWhite} 0%,
    rgba(241, 239, 234, 0.35) 100%
  );
`;

const EventsIntroInner = styled.div`
  max-width: 40rem;
  margin: 0 auto;
  text-align: center;
`;

const EventsIntroEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.48);
  margin: 0 0 0.65rem;
`;

const EventsIntroTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.75rem, 4vw, 2.35rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.85rem;
  line-height: 1.12;
`;

const EventsIntroSubtitle = styled.p`
  font-size: clamp(0.94rem, 1.85vw, 1.05rem);
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.58);
  margin: 0;
`;

const ContentContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 4vw, 2rem) clamp(3rem, 7vw, 4.5rem);
`;

const EventsPageSection = styled.section`
  padding: clamp(2.25rem, 5vw, 3.5rem) 0;
  border-top: 1px solid rgba(16, 29, 64, 0.07);

  &:first-of-type {
    border-top: none;
    padding-top: clamp(1.75rem, 4vw, 2.5rem);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 38rem;
  margin: 0 auto clamp(2rem, 4.5vw, 2.75rem);
`;

const SectionEyebrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
`;

const SectionEyebrowLine = styled.span`
  display: block;
  width: 2rem;
  height: 1.5px;
  background: ${({ theme }) => theme.colors.accent};
  border-radius: 2px;
  opacity: 0.85;
`;

const SectionEyebrow = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.48);
`;

const SectionHeading = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.85rem, 4vw, 2.45rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  line-height: 1.12;

  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 2px;
    margin: 1rem auto 0;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.accent},
      transparent
    );
  }
`;

const SectionLead = styled.p`
  font-size: clamp(0.95rem, 1.85vw, 1.05rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.58);
  margin: 1.25rem 0 0;
`;

const FeaturedGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1.2rem, 3vw, 1.55rem);
`;

const FeaturedSecondaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.15rem;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.25rem 1.35rem;
  }
`;

const featuredCardShadow = `
  0 1px 0 rgba(255, 255, 255, 0.92) inset,
  0 14px 42px rgba(16, 29, 64, 0.08)
`;

const featuredCardShadowHover = `
  0 1px 0 rgba(255, 255, 255, 0.97) inset,
  0 22px 52px rgba(16, 29, 64, 0.11)
`;

/* Hero: split image | copy on large screens (Day Tickets / editorial refresh) */
const MainFeaturedEvent = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(16, 29, 64, 0.09);
  background: #fff;
  box-shadow: ${featuredCardShadow};
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.28s ease,
    border-color 0.28s ease;

  @media (min-width: 880px) {
    grid-template-columns: minmax(0, 1.08fr) minmax(0, 1fr);
    min-height: min(440px, 56vw);
    align-items: stretch;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(157, 109, 73, 0.32);
    box-shadow: ${featuredCardShadowHover};
  }
`;

const FeaturedHeroVisual = styled.div`
  position: relative;
  min-height: 220px;
  background-image: url("${props => props.$image}");
  background-size: cover;
  background-position: center;

  @media (min-width: 880px) {
    min-height: 100%;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-bottom: 1px solid rgba(16, 29, 64, 0.06);
    pointer-events: none;

    @media (min-width: 880px) {
      border-bottom: none;
      border-right: 1px solid rgba(16, 29, 64, 0.06);
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      165deg,
      rgba(8, 14, 38, 0.35) 0%,
      transparent 42%,
      rgba(8, 14, 38, 0.25) 100%
    );
    pointer-events: none;

    @media (min-width: 880px) {
      background: linear-gradient(
        95deg,
        rgba(8, 14, 38, 0.42) 0%,
        transparent 48%,
        rgba(250, 249, 247, 0.08) 100%
      );
    }
  }
`;

const FeaturedHeroPanel = styled.div`
  position: relative;
  padding: clamp(1.45rem, 3.2vw, 2.25rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
  background: linear-gradient(
    168deg,
    #ffffff 0%,
    #faf9f6 52%,
    #f4f1ea 100%
  );

  @media (max-width: 879px) {
    border-top: 1px solid rgba(16, 29, 64, 0.06);
  }
`;

/* Compact featured cards: horizontal split from ~520px */
const FeaturedEvent = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(16, 29, 64, 0.09);
  background: #fff;
  box-shadow: ${featuredCardShadow};
  transition: transform 0.26s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.26s ease,
    border-color 0.26s ease;

  @media (min-width: 520px) {
    grid-template-columns: minmax(148px, 40%) minmax(0, 1fr);
    min-height: 220px;
    align-items: stretch;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(157, 109, 73, 0.3);
    box-shadow: ${featuredCardShadowHover};
  }
`;

const FeaturedCompactVisual = styled.div`
  position: relative;
  min-height: 200px;
  background-image: url("${props => props.$image}");
  background-size: cover;
  background-position: center;

  @media (min-width: 520px) {
    min-height: 100%;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(8, 14, 38, 0.12) 0%,
      transparent 40%,
      rgba(8, 14, 38, 0.28) 100%
    );
    pointer-events: none;

    @media (min-width: 520px) {
      background: linear-gradient(
        90deg,
        rgba(8, 14, 38, 0.35) 0%,
        transparent 65%
      );
    }
  }
`;

const FeaturedCompactPanel = styled.div`
  flex: 1;
  min-height: 0;
  padding: 1.2rem 1.3rem 1.35rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: linear-gradient(180deg, #ffffff 0%, #faf9f7 100%);
  border-top: 1px solid rgba(16, 29, 64, 0.05);

  @media (min-width: 520px) {
    border-top: none;
    border-left: 1px solid rgba(16, 29, 64, 0.06);
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: ${props => (props.$compact ? '0.75rem' : '1rem')};
  right: ${props => (props.$compact ? '0.75rem' : '1rem')};
  z-index: 2;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: ${props => (props.$compact ? '0.35rem 0.7rem' : '0.45rem 0.95rem')};
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: ${props => (props.$compact ? '0.625rem' : '0.6875rem')};
  font-weight: 600;
  letter-spacing: ${props => (props.$compact ? '0.06em' : '0.08em')};
  text-transform: uppercase;
  display: flex;
  align-items: center;
  box-shadow: 0 6px 18px rgba(16, 29, 64, 0.14);

  svg {
    margin-right: ${props => (props.$compact ? '0.3rem' : '0.45rem')};
    opacity: 0.95;
  }
`;

const FeaturedMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 1.15rem;
  row-gap: 0.45rem;
  margin-bottom: 0.65rem;
`;

const FeaturedMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(16, 29, 64, 0.58);

  svg {
    color: ${props => props.theme.colors.accent};
    opacity: 0.92;
    flex-shrink: 0;
  }
`;

const FeaturedExcerpt = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.55vw, 0.95rem);
  line-height: 1.58;
  margin: 0.65rem 0 1rem;
  color: rgba(16, 29, 64, 0.66);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EventTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: ${props => props.$main ? 'clamp(1.45rem, 3vw, 2rem)' : 'clamp(1.2rem, 2.4vw, 1.5rem)'};
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  line-height: 1.15;
`;

const EventSubtitle = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: ${props => props.$main ? '1rem' : '0.9rem'};
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  margin-bottom: 1rem;
`;

const EventDescription = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  line-height: 1.62;
  margin: 1.25rem 0;
  color: rgba(16, 29, 64, 0.72);
`;

const EventActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: ${props => (props.$pushDown ? 'auto' : '0.25rem')};
`;

const EventButton = styled.button`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  border: 2px solid transparent;
  border-radius: 999px;
  padding: 0.65rem 1.35rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;

  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const AddToCalendarButton = styled.button`
  background-color: transparent;
  color: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 999px;
  padding: 0.65rem 1.35rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;

  &:hover {
    background-color: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(1.5rem, 3vw, 2rem);
  flex-wrap: wrap;
  gap: 1rem 1.25rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: min(100%, 280px);
  max-width: 380px;
  background-color: #fff;
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 12px;
  padding: 0.6rem 1rem;
  box-shadow: 0 1px 2px rgba(16, 29, 64, 0.04);

  svg {
    color: rgba(16, 29, 64, 0.35);
    margin-right: 0.55rem;
    flex-shrink: 0;
  }

  input {
    border: none;
    outline: none;
    width: 100%;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.95rem;
    color: ${props => props.theme.colors.primary};
    background: transparent;

    &::placeholder {
      color: rgba(16, 29, 64, 0.38);
    }
  }

  &:focus-within {
    border-color: rgba(157, 109, 73, 0.45);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }
`;

const FilterDropdown = styled.select`
  padding: 0.6rem 1rem;
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 12px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  background-color: #fff;
  cursor: pointer;
  color: ${props => props.theme.colors.primary};
  box-shadow: 0 1px 2px rgba(16, 29, 64, 0.04);

  &:focus {
    outline: none;
    border-color: rgba(157, 109, 73, 0.45);
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }
`;

const FilterToolsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const FilterLabel = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  svg {
    color: ${props => props.theme.colors.accent};
    opacity: 0.85;
  }
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem 1.35rem;
  margin-top: 0;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PirateTakeoverBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: linear-gradient(135deg, #1a3a52 0%, #0d1f2d 100%);
  color: #f4d03f;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid rgba(244, 208, 63, 0.5);
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  margin-bottom: 0.5rem;
  width: fit-content;
  span[aria-hidden] {
    font-size: 1.1em;
    line-height: 1;
  }
`;

const EventCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid
    ${props =>
      props.$isPirateTakeover ? 'rgba(26, 58, 82, 0.35)' : 'rgba(16, 29, 64, 0.08)'};
  background: ${props =>
    props.$isPirateTakeover
      ? 'linear-gradient(180deg, #fffbeb 0%, #ffffff 14%)'
      : '#fff'};
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 12px 36px rgba(16, 29, 64, 0.07);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${props =>
      props.$isPirateTakeover ? 'rgba(244, 208, 63, 0.65)' : 'rgba(157, 109, 73, 0.28)'};
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 18px 44px rgba(16, 29, 64, 0.1);
  }
`;

const CardImage = styled.div`
  height: 200px;
  background-image: url("${props => props.$image}");
  background-size: cover;
  background-position: center;
`;

const CardContent = styled.div`
  padding: 1.35rem 1.35rem 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.15rem, 2.1vw, 1.35rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.45rem;
  line-height: 1.2;
`;

const CardSubtitle = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  margin-bottom: 0.85rem;
`;

const CardInfo = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.4rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.85rem;
  color: rgba(16, 29, 64, 0.62);

  svg {
    margin-right: 0.45rem;
    margin-top: 0.15rem;
    color: ${props => props.theme.colors.accent};
    flex-shrink: 0;
  }
`;

const CardDescription = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.6vw, 0.95rem);
  line-height: 1.58;
  margin: 0.85rem 0 1rem;
  color: rgba(16, 29, 64, 0.65);
  flex: 1;
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: auto;
`;

const CardButton = styled.button`
  background-color: transparent;
  color: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 999px;
  padding: 0.5rem 1rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  display: inline-flex;
  align-items: center;
  
  svg {
    margin-left: 0.5rem;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const LoadMoreButton = styled.button`
  background-color: transparent;
  color: ${props => props.theme.colors.primary};
  border: 2px solid rgba(16, 29, 64, 0.25);
  border-radius: 999px;
  padding: 0.75rem 1.75rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  margin: clamp(2rem, 5vw, 3rem) auto 0;
  display: block;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    border-color: ${props => props.theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NoEventsMessage = styled.div`
  text-align: center;
  padding: clamp(2rem, 5vw, 2.75rem) clamp(1.25rem, 4vw, 2rem);
  border-radius: 16px;
  border: 1px solid rgba(16, 29, 64, 0.08);
  background: #fff;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 12px 36px rgba(16, 29, 64, 0.06);
  margin: 0;

  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: clamp(1.35rem, 3vw, 1.65rem);
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.65rem;
  }

  p {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: clamp(0.95rem, 1.85vw, 1.05rem);
    line-height: 1.65;
    color: rgba(16, 29, 64, 0.58);
    margin: 0;
  }
`;

// Calendar view — matching page chrome
const ViewToggleContainer = styled.div`
  display: inline-flex;
  border-radius: 999px;
  border: 1px solid rgba(16, 29, 64, 0.1);
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 29, 64, 0.04);
`;

const ViewToggleButton = styled.button`
  background-color: ${props => (props.$active ? props.theme.colors.accent : 'transparent')};
  color: ${props => (props.$active ? '#fff' : props.theme.colors.primary)};
  border: none;
  padding: 0.55rem 1.1rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;

  svg {
    opacity: ${props => (props.$active ? 1 : 0.75)};
  }

  &:hover {
    background-color: ${props =>
      props.$active ? props.theme.colors.accent : 'rgba(16, 29, 64, 0.05)'};
  }
`;

const CalendarContainer = styled.div`
  margin: 0;
  padding: clamp(1.25rem, 3vw, 1.75rem);
  background-color: #fff;
  border-radius: 16px;
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 12px 36px rgba(16, 29, 64, 0.06);
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.35rem;
  flex-wrap: wrap;
  gap: 0.75rem;

  h2 {
    margin: 0;
    font-family: ${props => props.theme.fonts.main};
    font-size: clamp(1.35rem, 2.8vw, 1.65rem);
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
  }
`;

const CalendarMonthTitle = styled.h3`
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.15rem, 2.4vw, 1.4rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const CalendarControls = styled.div`
  display: flex;
  gap: 0.45rem;

  button {
    padding: 0.45rem 0.85rem;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.8125rem;
    font-weight: 600;
    background-color: rgba(16, 29, 64, 0.04);
    border: 1px solid rgba(16, 29, 64, 0.1);
    border-radius: 10px;
    cursor: pointer;
    color: ${props => props.theme.colors.primary};
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover {
      background-color: rgba(157, 109, 73, 0.12);
      border-color: rgba(157, 109, 73, 0.35);
    }
  }
`;

const CalendarDayContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const EventDot = styled.div`
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${props => {
    if (props.$type === 'castle') return '#3498db';
    if (props.$type === 'garden' || props.$type === 'parkland') return '#2ecc71';
    if (props.$type === 'retail') return '#e74c3c';
    return '#95a5a6'; // default/other
  }};
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
  margin-top: 1.35rem;
  padding: 1rem 1.15rem;
  background-color: rgba(16, 29, 64, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(16, 29, 64, 0.06);
`;

const LegendTitle = styled.h4`
  width: 100%;
  margin: 0 0 0.35rem 0;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.85rem;
  color: rgba(16, 29, 64, 0.65);
`;

const LegendColor = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-right: 8px;
`;

// Add global styles for the DatePicker calendar days
const GlobalStyle = createGlobalStyle`
  .castle-event-day {
    background-color: rgba(52, 152, 219, 0.2) !important;
    border-radius: 50%;
    color: #333 !important;
    font-weight: bold;
    
    &:hover {
      background-color: rgba(52, 152, 219, 0.4) !important;
    }
  }
  
  .parkland-event-day {
    background-color: rgba(139, 195, 74, 0.2) !important;
    border-radius: 50%;
    color: #333 !important;
    font-weight: bold;
    
    &:hover {
      background-color: rgba(139, 195, 74, 0.4) !important;
    }
  }
  
  .garden-event-day {
    background-color: rgba(46, 204, 113, 0.2) !important;
    border-radius: 50%;
    color: #333 !important;
    font-weight: bold;
    
    &:hover {
      background-color: rgba(46, 204, 113, 0.4) !important;
    }
  }
  
  .retail-event-day {
    background-color: rgba(231, 76, 60, 0.2) !important;
    border-radius: 50%;
    color: #333 !important;
    font-weight: bold;
    
    &:hover {
      background-color: rgba(231, 76, 60, 0.4) !important;
    }
  }
  
  .other-event-day {
    background-color: rgba(149, 165, 166, 0.2) !important;
    border-radius: 50%;
    color: #333 !important;
    font-weight: bold;
    
    &:hover {
      background-color: rgba(149, 165, 166, 0.4) !important;
    }
  }
  
  .react-datepicker {
    font-family: 'Montserrat', sans-serif;
    border: none;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 100%;
  }
  
  .react-datepicker__month-container {
    width: 100%;
  }
  
  .react-datepicker__day {
    margin: 0.2rem;
    width: 2.5rem;
    height: 2.5rem;
    line-height: 2.5rem;
    border-radius: 50%;
    
    &:hover {
      background-color: #f0f0f0;
    }
  }
  
  .react-datepicker__day--selected {
    background-color: #101d40 !important;
    color: white !important;
  }
  
  .react-datepicker__header {
    background-color: rgba(16, 29, 64, 0.03);
    border-bottom: 1px solid rgba(16, 29, 64, 0.06);
  }
  
  .react-datepicker__day-name {
    width: 2.5rem;
    margin: 0.2rem;
  }
`;

// Update the EventTooltip component
const EventTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  width: 250px;
  z-index: 100;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px;
    border-style: solid;
    border-color: white transparent transparent transparent;
  }
`;

const TooltipTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem 0;
`;

const TooltipInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.3rem;
  
  svg {
    margin-right: 0.5rem;
    color: ${props => props.theme.colors.accent};
  }
`;

const TooltipEventsList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  margin-top: 0.5rem;
`;

const TooltipEvent = styled.div`
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

// Update the TooltipViewButton component
const TooltipViewButton = styled.button`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  border: 2px solid transparent;
  border-radius: 25px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  cursor: pointer;
  width: 100%;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

// Add this component after all the styled components and before the Events component
const CalendarDay = ({ day, date, events, handleEventClick }) => {
  const dayRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Skip rendering if no events
  if (events.length === 0) return day;
  
  // Determine event type for the dot color
  let eventType = 'other';
  if (events[0].location) {
    const location = events[0].location.toLowerCase();
    if (location.includes('castle')) {
      eventType = 'castle';
    } else if (location.includes('garden') || location.includes('parkland')) {
      eventType = 'garden';
    } else if (location.includes('retail') || location.includes('shop')) {
      eventType = 'retail';
    }
  }
  
  // Format the date for display
  const formattedDate = format(date, 'EEEE, MMMM d, yyyy');
  
  // Handle button click without closing tooltip
  const handleButtonClick = (eventId, e) => {
    e.stopPropagation(); // Prevent the click from bubbling up
    handleEventClick(eventId);
  };
  
  return (
    <CalendarDayContent 
      ref={dayRef}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {day}
      {events.length > 0 && (
        <EventDot $type={eventType}>
          {events.length > 1 ? events.length : ''}
        </EventDot>
      )}
      
      <EventTooltip $visible={showTooltip}>
        <TooltipTitle>{formattedDate}</TooltipTitle>
        <TooltipInfo>
          <FontAwesomeIcon icon={faCalendarAlt} />
          {events.length} {events.length === 1 ? 'Event' : 'Events'}
        </TooltipInfo>
        
        <TooltipEventsList>
          {events.map((event, index) => (
            <TooltipEvent key={index}>
              <TooltipTitle>{event.title}</TooltipTitle>
              <TooltipInfo>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                {event.location || 'Belvoir Castle'}
              </TooltipInfo>
              {event.ticketTier && (
                <TooltipInfo>
                  <FontAwesomeIcon icon={faTicketAlt} />
                  {event.ticketTier}
                </TooltipInfo>
              )}
              <TooltipViewButton onClick={(e) => handleButtonClick(event, e)}>
                View Details
              </TooltipViewButton>
            </TooltipEvent>
          ))}
        </TooltipEventsList>
      </EventTooltip>
    </CalendarDayContent>
  );
};

// Helper function to get the 3rd Sunday of a given month
const getThirdSunday = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const firstSunday = firstDay.getDay() === 0 ? 1 : (7 - firstDay.getDay() + 1);
  const thirdSunday = firstSunday + 14;
  return new Date(year, month, thirdSunday);
};

// Generate Antiques Market event (single list entry; calendar uses third Sundays only — not the full Apr–Sep date span)
const generateAntiquesMarketEvents = () => {
  const year = 2026;
  const firstDate = getThirdSunday(year, 3); // April 3rd Sunday
  const lastDate = getThirdSunday(year, 8); // September 3rd Sunday

  return [{
    id: 'antiques-artisan-market-2026',
    title: 'Vintage & Craft Market',
    subtitle: '3rd Sunday Every Month | April - September',
    date: '3rd Sunday Every Month | April - September',
    description: 'A monthly celebration of craftsmanship, character and timeless finds. Browse curated stalls featuring local artists, makers, antiques and collectables. Free entry.',
    image: retailImage,
    location: 'RETAIL VILLAGE',
    link: '/events/antiques-artisan-market',
    ticketTier: 'Free Entry',
    /** Calendar: show only on 3rd Sunday of these months (0–11), not every day between eventDate and eventEndDate */
    calendarThirdSundayMonths: [3, 4, 5, 6, 7, 8],
    eventDetails: {
      eventDate: firstDate.toISOString().split('T')[0],
      eventEndDate: lastDate.toISOString().split('T')[0],
      eventTime: '10:00 - 16:00',
      eventTags: ['market', 'family', 'outdoor', 'food'],
    }
  }];
};

const Events = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [normalizedEvents, setNormalizedEvents] = useState([]);
  
  // Use the What's On section hook
  useWhatsOnSection();
  
  // Fetch all events
  const { loading: eventsLoading, error: eventsError, data: eventsData } = useQuery(GET_EVENTS_QUERY);
  
  // Fetch featured events - using the first 3 events as featured
  const { loading: featuredLoading, error: featuredError, data: featuredData } = useQuery(GET_FEATURED_EVENTS_QUERY);
  
  // Process events data when it's loaded
  useEffect(() => {
    if (eventsData && eventsData.events && eventsData.events.nodes) {
      // Generate Antiques Market events
      const antiquesMarketEvents = generateAntiquesMarketEvents();
      
      // Hardcoded Christmas 2026 events
      const christmasEvents = [
        {
          id: 'santas-grotto-castle-2026',
          title: "Santa's Grotto — Castle",
          date: 'Saturday, November 14, 2026 to Thursday, December 24, 2026',
          description: 'Meet Father Christmas inside Belvoir Castle during Deck the Halls. A private family visit, with a present for every child. Castle entry required.',
          image: imgCastleGrotto,
          location: 'CASTLE',
          link: '/christmas/santas-grotto-castle',
          eventDetails: { eventDate: '2026-11-14', eventEndDate: '2026-12-24' }
        },
        {
          id: 'santas-grotto-village-2026',
          title: "Santa's Grotto — Retail Village",
          date: 'Saturday, November 21, 2026 to Thursday, December 24, 2026',
          description: "Meet Father Christmas at Belvoir Retail Village. Children's tickets only — no Castle entry required. Present for every child.",
          image: imgVillageGrotto,
          location: 'RETAIL VILLAGE',
          link: '/christmas/santas-grotto-retail-village-options',
          eventDetails: { eventDate: '2026-11-21', eventEndDate: '2026-12-24' }
        },
        {
          id: null,
          title: 'Search for Santa',
          date: 'Available on selected dates throughout December — click here to find out more',
          description: 'Exclusive late afternoon family trail through the Castle to find Santa. Hot chocolate, present and bubbles. Full-day Castle access included.',
          image: imgSearch,
          location: 'CASTLE',
          link: '/christmas/search-for-santa',
          eventDetails: { eventDate: '2026-12-05T17:00:00', eventEndDate: '2026-12-23T19:30:00' }
        },
        {
          id: null,
          title: 'Festive Mice Trail',
          date: 'Saturday, November 14, 2026 to Wednesday, December 30, 2026',
          description: 'Follow the delightful Mice Trail through the Castle\'s carol-themed grand rooms. Included with Deck the Halls entry.',
          image: imgMiceTrail,
          location: 'CASTLE',
          link: '/christmas/mice-trail',
          eventDetails: { eventDate: '2026-11-14', eventEndDate: '2026-12-30' }
        },
        {
          id: null,
          title: 'Deck The Halls — Belvoir Castle by Candlelight',
          date: 'December Thursdays + 22 Dec 2026',
          description: 'Adults-only Christmas evening. Live musicians, bubbles and mince pie on arrival. Free-flow Deck the Halls by candlelight. £40 per person.',
          image: imgCandlelight,
          location: 'CASTLE',
          link: '/christmas/castle-by-candlelight',
          eventDetails: { eventDate: '2026-12-03T18:00:00', eventEndDate: '2026-12-22T20:30:00' }
        },
        {
          id: 'wreath-making-workshop-2026',
          title: 'Wreath Making Workshop',
          date: 'Saturday 28 November 2026 · 1pm',
          description: 'Join florist Sarah Youngman from Sage & Satin Florals for an afternoon of wreath making upstairs in the Wellness Studio. Materials are provided, no experience is needed, and your ticket includes a mince pie and mulled wine. About 2.5 hours. 20 places.',
          image: imgWreath,
          location: 'RETAIL VILLAGE',
          link: '/whats-on/wreath-making',
          linkText: 'View details',
          ticketTier: '£70',
          price: '£70',
          eventTags: ['christmas', 'workshop', 'wellness'],
          eventDetails: { eventDate: '2026-11-28', eventEndDate: '2026-11-28', eventTime: '1pm start (2.5 hours)' }
        },
        {
          id: 'meet-santas-reindeer',
          title: "Meet Santa's Reindeer",
          date: 'Saturday 19 December 2026 · 10am – 2pm',
          description: "Step into the magic of Christmas at Belvoir Retail Village and meet two of Santa's very own reindeer!",
          image: 'https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/reindeer.webp',
          location: 'RETAIL VILLAGE',
          link: '/events/meet-santas-reindeer',
          price: 'No tickets required',
          eventDetails: { eventDate: '2026-12-19', eventEndDate: '2026-12-19', eventTime: '10am – 2pm' }
        },
        {
          id: null,
          title: 'Festive Afternoon Tea',
          date: 'Saturday, November 14, 2026 to Wednesday, December 30, 2026',
          description: 'Belvoir Castle is the home of afternoon tea. Festive Afternoon Tea in the Aviary Tearoom during Deck the Halls — add-on to Castle entry.',
          image: imgAfternoonTea,
          location: 'CASTLE',
          link: '/christmas/festive-afternoon-tea',
          eventDetails: { eventDate: '2026-11-14', eventEndDate: '2026-12-30' }
        }
      ];
      
      // Hardcoded Halloween — trail + crafts
      const halloweenEvents = [
        {
          id: 'belvoir-wicked-night-2026',
          title: "Belvoir's Enchanted Halloween Trail",
          subtitle: 'October School Holidays 2026',
          date: 'October School Holidays 2026',
          description: 'A pesky witch has cursed Belvoir. Collect spell ingredients, find the password animal in the Animal Park, and brew the counter-curse at the cauldron. A family Halloween adventure trail — silly, not scary. Animal Park included with every ticket.',
          image: halloweenHeroImage,
          location: 'Estate',
          link: '/halloween-belvoir-castle',
          ticketTier: 'Book online',
          eventTags: ['family', 'trail', 'halloween'],
          eventDetails: {
            eventDate: '2026-10-03',
            eventEndDate: '2026-10-31',
          }
        },
        {
          id: 'belvoir-halloween-crafts-2026',
          title: 'Halloween Crafts',
          subtitle: 'October School Holidays 2026 · £4.50',
          date: 'October School Holidays 2026',
          description: 'Hands-on Halloween crafts in the Retail Village. £4.50. Book standalone or add to your Enchanted Halloween Trail visit. Take the magic home.',
          image: halloweenCraftsImage,
          location: 'Retail Village',
          link: '/halloween-crafts',
          ticketTier: '£4.50',
          eventTags: ['family', 'crafts', 'halloween'],
          eventDetails: {
            eventDate: '2026-10-03',
            eventEndDate: '2026-10-31',
          }
        }
      ];

      const harvestEvents = [
        {
          id: 'belvoir-harvest-day-2026',
          title: 'Harvest Day',
          subtitle: 'Sunday 4 October 2026',
          date: 'Sunday 4 October 2026 · 11:00–15:30',
          description: 'Celebrate the season at Belvoir Retail Village. Live music with Yvonne Rivers, farm animals, Farm Shop tastings, complimentary Gun Bus tours, and a Harvest Service at 3:30pm. Refreshments included.',
          image: harvestHeroImage,
          location: 'Retail Village',
          link: '/whats-on/harvest-day',
          ticketTier: 'Free',
          eventTags: ['family', 'music', 'harvest'],
          eventDetails: {
            eventDate: '2026-10-04',
            eventEndDate: '2026-10-04',
            eventTime: '11:00 – 15:30',
          }
        }
      ];

      // Hardcoded Vineyard Tours
      const vineyardEvents = [
        {
          id: 'belvoir-vineyard-tours-2026',
          title: 'Vineyard Tours',
          subtitle: 'Saturday 29 August 2026',
          date: 'Saturday 29 August 2026 · 12:00–14:30 & 15:00–17:30',
          description: 'Discover the art of wine making with expert guide Alasdair Lawson-Dick. Gun Bus through the estate, tour the vines with our Estates Team, and sample 4 estate wines with personal tasting boxes. Over 18s only.',
          image: vineyardImage,
          location: 'Retail Village Farm Shop',
          link: '/whats-on/vineyard-tour',
          ticketTier: 'Book online',
          eventTags: ['tour', 'wine', 'adult'],
          eventDetails: {
            eventDate: '2026-08-29',
            eventEndDate: '2026-08-29',
            eventTime: '12:00 – 14:30 & 15:00 – 17:30',
          }
        }
      ];

      const wellnessEvents = [
        {
          id: 'belvoir-wellness-day-2026',
          title: 'Belvoir Wellness — A Day of Discovery',
          subtitle: 'Sunday 20 September 2026',
          date: 'Sunday 20 September 2026 · 10:00–17:00',
          description: 'Her Grace invites you to Belvoir Castle for Mind, Body, Belvoir — yoga, sound baths, Chapel Services, Mind Body Belvoir Studio, and Sensory Tree Trail Tours. Castle entry at regular admission; wellness activities free.',
          image: 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%203%20(1).webp',
          location: 'Castle, Retail Village and Gardens',
          link: '/whats-on/belvoir-wellness',
          linkText: 'Discover Belvoir Wellness',
          ticketTier: 'Book online',
          eventTags: ['wellness', 'castle', 'nature'],
          eventDetails: {
            eventDate: '2026-09-20',
            eventEndDate: '2026-09-20',
            eventTime: '10:00 – 17:00',
          }
        }
      ];

      // Combine all hardcoded events
      const hardcodedEvents = [...halloweenEvents, ...harvestEvents, ...vineyardEvents, ...wellnessEvents, ...antiquesMarketEvents, ...christmasEvents];
      
      // Combine and sort events by date
      const combinedEvents = combineAndSortEvents(hardcodedEvents, eventsData.events.nodes);
      
      // Apply filters
      let filteredEvents = [...combinedEvents];
      
      if (searchTerm) {
        filteredEvents = filteredEvents.filter(event => 
          event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      if (filter !== 'all') {
        filteredEvents = filteredEvents.filter(event => 
          event.location && event.location.toLowerCase().includes(filter.toLowerCase())
        );
      }
      
      setDisplayedEvents(filteredEvents);
    }
  }, [eventsData, searchTerm, filter]);
  
  // Update the useEffect for normalizing events
  useEffect(() => {
    if (displayedEvents.length > 0) {
      // Create a copy of events with normalized date properties
      const normalized = displayedEvents.map(event => {
        // Create a copy of the event
        const normalizedEvent = { ...event };
        
        try {
          // Extract the date information
          let eventStartDate = null;
          let eventEndDate = null;
          
          // Check if we have event details with dates
          if (event.eventDetails && event.eventDetails.eventDate) {
            eventStartDate = new Date(event.eventDetails.eventDate);
            
            if (event.eventDetails.eventEndDate) {
              eventEndDate = new Date(event.eventDetails.eventEndDate);
            } else {
              eventEndDate = new Date(eventStartDate); // Same day for single-day events
            }
            
            if (!isNaN(eventStartDate.getTime()) && !isNaN(eventEndDate.getTime())) {
              normalizedEvent.normalizedStartDate = eventStartDate;
              normalizedEvent.normalizedEndDate = eventEndDate;
            }
          }
          
          // If we don't have valid dates from eventDetails, try parsing the date string
          if (!normalizedEvent.normalizedStartDate || !normalizedEvent.normalizedEndDate) {
            // Handle date ranges (e.g., "4 - 13 Mar 2025")
            if (event.date && event.date.includes(' - ')) {
              const dateStr = event.date;
              
              // Extract the start day, end day, month, and year
              const match = dateStr.match(/(\d+)\s*-\s*(\d+)\s+([A-Za-z]+)\s+(\d{4})/);
              
              if (match) {
                const startDay = parseInt(match[1], 10);
                const endDay = parseInt(match[2], 10);
                const month = getMonthNumber(match[3]);
                const year = parseInt(match[4], 10);
                
                if (!isNaN(startDay) && !isNaN(endDay) && month !== -1 && !isNaN(year)) {
                  // Create start and end dates
                  eventStartDate = new Date(year, month, startDay);
                  eventEndDate = new Date(year, month, endDay);
                  
                  if (!isNaN(eventStartDate.getTime()) && !isNaN(eventEndDate.getTime())) {
                    normalizedEvent.normalizedStartDate = eventStartDate;
                    normalizedEvent.normalizedEndDate = eventEndDate;
                  }
                }
              }
            } else if (event.date) {
              // Handle single date
              const dateParts = event.date.split(' ');
              if (dateParts.length >= 3) {
                const day = parseInt(dateParts[0], 10);
                const month = getMonthNumber(dateParts[1]);
                const year = parseInt(dateParts[2], 10);
                
                if (!isNaN(day) && month !== -1 && !isNaN(year)) {
                  eventStartDate = new Date(year, month, day);
                  eventEndDate = new Date(year, month, day);
                  
                  if (!isNaN(eventStartDate.getTime()) && !isNaN(eventEndDate.getTime())) {
                    normalizedEvent.normalizedStartDate = eventStartDate;
                    normalizedEvent.normalizedEndDate = eventEndDate;
                  }
                }
              }
            }
          }
          
          // Add a formatted date string for debugging
          if (normalizedEvent.normalizedStartDate && normalizedEvent.normalizedEndDate) {
            normalizedEvent.normalizedDateStr = format(normalizedEvent.normalizedStartDate, 'yyyy-MM-dd');
            
            if (!isSameDay(normalizedEvent.normalizedStartDate, normalizedEvent.normalizedEndDate)) {
              normalizedEvent.normalizedDateStr += ` to ${format(normalizedEvent.normalizedEndDate, 'yyyy-MM-dd')}`;
            }
          }
          
          return normalizedEvent;
        } catch (error) {

          return normalizedEvent;
        }
      });
      
      setNormalizedEvents(normalized);
    }
  }, [displayedEvents]);
  
  // Update calendar view when currentDate changes
  useEffect(() => {
    if (viewMode === 'calendar') {

      // Count events for the current month
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      const eventsThisMonth = normalizedEvents.filter(event => {
        if (event.normalizedStartDate) {
          const eventDate = new Date(event.normalizedStartDate);
          return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
        }
        return false;
      });

    }
  }, [currentDate, viewMode, normalizedEvents]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setVisibleCount(6); // Reset visible count when search changes
  };
  
  // Handle filter change
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setVisibleCount(6); // Reset visible count when filter changes
  };
  
  // Handle load more button click
  const handleLoadMore = () => {
    setVisibleCount(prevCount => prevCount + 6);
  };
  
  // Handle event click (supports hardcoded events with direct links)
  const handleEventClick = (eventOrId) => {
    if (typeof eventOrId === 'object' && eventOrId !== null) {
      if (eventOrId.link) {
        window.location.assign(eventOrId.link);
        return;
      }
      if (eventOrId.id) {
        // Hardcoded pages that don't use /event/:id
        if (eventOrId.id === 'antiques-artisan-market-2026') {
          navigate('/events/antiques-artisan-market');
          return;
        }
        if (eventOrId.id === 'belvoir-vineyard-tours-2026') {
          navigate('/whats-on/vineyard-tour');
          return;
        }
        if (eventOrId.id === 'belvoir-wicked-night-2026') {
          navigate('/halloween-belvoir-castle');
          return;
        }
        if (eventOrId.id === 'belvoir-halloween-crafts-2026') {
          navigate('/halloween-crafts');
          return;
        }
        if (eventOrId.id === 'belvoir-wellness-day-2026') {
          navigate('/whats-on/belvoir-wellness');
          return;
        }
        if (eventOrId.id === 'belvoir-harvest-day-2026') {
          navigate('/whats-on/harvest-day');
          return;
        }
        navigate(`/event/${eventOrId.id}`);
        return;
      }
    }
    if (eventOrId) {
      // When only id string is passed (e.g. from calendar), route hardcoded events correctly
      if (eventOrId === 'antiques-artisan-market-2026') {
        navigate('/events/antiques-artisan-market');
        return;
      }
      if (eventOrId === 'belvoir-vineyard-tours-2026') {
        navigate('/whats-on/vineyard-tour');
        return;
      }
      if (eventOrId === 'belvoir-wicked-night-2026') {
        navigate('/halloween-belvoir-castle');
        return;
      }
      if (eventOrId === 'belvoir-halloween-crafts-2026') {
        navigate('/halloween-crafts');
        return;
      }
      if (eventOrId === 'belvoir-wellness-day-2026') {
        navigate('/whats-on/belvoir-wellness');
        return;
      }
      if (eventOrId === 'belvoir-harvest-day-2026') {
        navigate('/whats-on/harvest-day');
        return;
      }
      if (eventOrId === 'wreath-making-workshop-2026') {
        navigate('/whats-on/wreath-making');
        return;
      }
      navigate(`/event/${eventOrId}`);
    }
  };
  
  // Process featured events
  const featuredEvents = featuredData && featuredData.events && featuredData.events.nodes
    ? processEvents(featuredData.events.nodes).filter(event => event.location !== "PARKLAND")
    : [];
  
  // Update the process featured events section to respect the isFeatured flag
  // Replace the effectiveFeaturedEvents constant with this:
  const effectiveFeaturedEvents = featuredEvents.length > 0
    ? featuredEvents
    : displayedEvents.filter(event => 
        event.eventDetails && 
        event.eventDetails.isFeatured && 
        event.location !== "PARKLAND"
      ).slice(0, 3);
  
  // If still no featured events, use the first 3 regular events as a fallback
  const finalFeaturedEvents = effectiveFeaturedEvents.length > 0
    ? effectiveFeaturedEvents
    : displayedEvents.filter(event => event.location !== "PARKLAND").slice(0, 3);

  // Pin BOOM Circus as the primary featured event when present
  const circusFeaturedEvent = displayedEvents.find(event => event.id === 'cG9zdDo0MA==');
  if (circusFeaturedEvent) {
    const filteredFeaturedEvents = finalFeaturedEvents.filter(event => event.id !== 'cG9zdDo0MA==');
    finalFeaturedEvents.length = 0;
    finalFeaturedEvents.push(circusFeaturedEvent, ...filteredFeaturedEvents.slice(0, 2));
  }

  // Pin Belvoir's Enchanted Halloween Trail as the primary featured event
  const halloweenEvent = displayedEvents.find(event => event.id === 'belvoir-wicked-night-2026');
  if (halloweenEvent) {
    const filteredFeaturedEvents = finalFeaturedEvents.filter(event => event.id !== 'belvoir-wicked-night-2026');
    finalFeaturedEvents.length = 0;
    finalFeaturedEvents.push(halloweenEvent, ...filteredFeaturedEvents.slice(0, 2));
  }

  // Pin The Flower and Garden Show as the second featured event
  const flowerShowEvent = displayedEvents.find(
    event => event.title && event.title.toLowerCase().includes('flower and garden show')
  );
  if (flowerShowEvent) {
    const withoutFlowerShow = finalFeaturedEvents.filter(event => event.id !== flowerShowEvent.id);
    finalFeaturedEvents.length = 0;
    finalFeaturedEvents.push(withoutFlowerShow[0], flowerShowEvent, withoutFlowerShow[1]);
    // Remove any undefined slots
    while (finalFeaturedEvents[finalFeaturedEvents.length - 1] === undefined) {
      finalFeaturedEvents.pop();
    }
  }

  // Pin the Wreath Making Workshop as the third featured event
  const wreathEvent = displayedEvents.find(event => event.id === 'wreath-making-workshop-2026');
  if (wreathEvent) {
    const withoutWreath = finalFeaturedEvents.filter(event => event.id !== wreathEvent.id);
    finalFeaturedEvents.length = 0;
    finalFeaturedEvents.push(...withoutWreath.slice(0, 2), wreathEvent);
  }

  // Find the Christmas event from all events
  const christmasEvent = eventsData && eventsData.events && eventsData.events.nodes
    ? processEvents(eventsData.events.nodes).find(event => event.id === 'cG9zdDoxNTI=')
    : null;

  // Exclude the Christmas event from the featured list to avoid duplication
  const featuredList = finalFeaturedEvents.filter(event => event.id !== 'cG9zdDoxNTI=');
  
  // Calendar navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(prevDate => subMonths(prevDate, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(prevDate => addMonths(prevDate, 1));
  };
  
  // Function to generate calendar days
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = new Date(monthStart);
    startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
    
    const endDate = new Date(monthEnd);
    const daysToAdd = 6 - endDate.getDay();
    endDate.setDate(endDate.getDate() + daysToAdd); // End on Saturday
    
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    return days;
  };
  
  // Update the getEventsForDay function to handle the case where only end date is available
  const getEventsForDay = (day) => {
    if (!day || normalizedEvents.length === 0) return [];
    
    return normalizedEvents.filter(event => {
      try {
        /* Vintage & Craft Market: occurs on 3rd Sunday each month Apr–Sep, not daily across the span */
        if (
          Array.isArray(event.calendarThirdSundayMonths) &&
          event.calendarThirdSundayMonths.length > 0
        ) {
          const y = day.getFullYear();
          const m = day.getMonth();
          if (!event.calendarThirdSundayMonths.includes(m)) return false;
          const thirdSun = getThirdSunday(y, m);
          return isSameDay(day, thirdSun);
        }

        // If we have both start and end dates, check if day is in range
        if (event.normalizedStartDate && event.normalizedEndDate) {
          const startDate = new Date(event.normalizedStartDate);
          const endDate = new Date(event.normalizedEndDate);
          
          // Add one day to endDate to make the range inclusive
          endDate.setDate(endDate.getDate() + 1);
          
          return day >= startDate && day < endDate;
        }
        
        // If we only have end date (based on the debug info)
        if (!event.normalizedStartDate && event.normalizedEndDate) {
          const endDate = new Date(event.normalizedEndDate);
          return isSameDay(day, endDate);
        }
        
        return false;
      } catch (error) {

        return false;
      }
    });
  };
  
  // Helper function to convert month abbreviation to number (0-11)
  const getMonthNumber = (monthAbbr) => {
    if (!monthAbbr) return -1;
    
    const months = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11,
      // Add full month names for better compatibility
      'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    
    // Handle case insensitivity
    const normalizedMonth = monthAbbr.charAt(0).toUpperCase() + monthAbbr.slice(1).toLowerCase();
    return months[normalizedMonth] !== undefined ? months[normalizedMonth] : -1;
  };
  
  // Function to generate an .ics file for adding to calendar
  const addToCalendar = (event) => {
    // Parse the event date
    const dateParts = event.date.split(' ');
    if (dateParts.length < 3) return;
    
    const eventDay = parseInt(dateParts[0], 10);
    const eventMonth = getMonthNumber(dateParts[1]);
    const eventYear = parseInt(dateParts[2], 10);
    
    if (isNaN(eventDay) || isNaN(eventMonth) || isNaN(eventYear)) return;
    
    const eventDate = new Date(eventYear, eventMonth, eventDay);
    const endDate = new Date(eventDate);
    endDate.setHours(eventDate.getHours() + 2); // Assume 2-hour event if no end time
    
    // Format dates for .ics file
    const formatDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, '');
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'CALSCALE:GREGORIAN',
      'PRODID:-//Belvoir Castle//Events Calendar//EN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `SUMMARY:${event.title}`,
      `DTSTART:${formatDate(eventDate)}`,
      `DTEND:${formatDate(endDate)}`,
      `LOCATION:${event.location || 'Belvoir Castle'}`,
      `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
      `URL:https://belvoircastle.com/event/${event.id}`,
      'STATUS:CONFIRMED',
      `UID:${event.id}@belvoircastle.com`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    // Create and download the .ics file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/\s+/g, '-').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Function to share event to device calendar using Web Share API (for mobile)
  const shareEvent = (event) => {
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: `${event.title} at ${event.location || 'Belvoir Castle'} on ${event.date}`,
        url: `https://belvoircastle.com/event/${event.id}`
      })
      .catch(() => {});
    } else {
      // Fallback to .ics download if Web Share API is not available
      addToCalendar(event);
    }
  };
  
  // Render calendar view
  const renderCalendarView = () => {
    // Define theme colors for the calendar legend
    const themeColors = {
      castle: '#3498db',
      parkland: '#8bc34a', // Light green for parkland
      garden: '#2ecc71',
      retail: '#e74c3c',
      other: '#95a5a6'
    };
    
    // Function to debug events
    const debugEvents = () => {


      // Count events by location
      const debugEventsByLocation = {
        castle: 0,
        parkland: 0,
        garden: 0,
        retail: 0,
        other: 0
      };
      
      // Count normalized events with valid dates
      let eventsWithValidDates = 0;
      
      normalizedEvents.forEach(event => {
        // Count by location
        if (event.location) {
          const location = event.location.toLowerCase();
          if (location.includes('castle')) {
            debugEventsByLocation.castle++;
          } else if (location.includes('parkland')) {
            debugEventsByLocation.parkland++;
          } else if (location.includes('garden')) {
            debugEventsByLocation.garden++;
          } else if (location.includes('retail') || location.includes('shop')) {
            debugEventsByLocation.retail++;
          } else {
            debugEventsByLocation.other++;
          }
        } else {
          debugEventsByLocation.other++;
        }
        
        // Count events with valid normalized dates
        if (event.normalizedStartDate && event.normalizedEndDate) {
          eventsWithValidDates++;
        }
      });


      // Check events for the current month
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      const eventsThisMonth = normalizedEvents.filter(event => {
        if (event.normalizedStartDate) {
          const eventDate = new Date(event.normalizedStartDate);
          return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
        }
        return false;
      });

      // Log the first 3 events with their normalized dates

      normalizedEvents.slice(0, 3).forEach((event, index) => {


      });

    };
    
    // Count events by location using normalized events for the current month
    const eventsByLocation = {
      castle: 0,
      parkland: 0,
      garden: 0,
      retail: 0,
      other: 0
    };
    
    // Only count events for the current month
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // Improved counting logic that checks both start and end dates
    normalizedEvents.forEach(event => {
      // Skip events without normalized dates
      if (!event.normalizedStartDate && !event.normalizedEndDate) return;
      
      // Check if event occurs in the current month
      let isInCurrentMonth = false;
      
      if (event.normalizedStartDate) {
        const startDate = new Date(event.normalizedStartDate);
        // If start date is in current month/year
        if (startDate.getMonth() === currentMonth && startDate.getFullYear() === currentYear) {
          isInCurrentMonth = true;
        }
      }
      
      if (!isInCurrentMonth && event.normalizedEndDate) {
        const endDate = new Date(event.normalizedEndDate);
        // If end date is in current month/year
        if (endDate.getMonth() === currentMonth && endDate.getFullYear() === currentYear) {
          isInCurrentMonth = true;
        }
      }
      
      // If event spans multiple months, check if current month is within range
      if (!isInCurrentMonth && event.normalizedStartDate && event.normalizedEndDate) {
        const startDate = new Date(event.normalizedStartDate);
        const endDate = new Date(event.normalizedEndDate);
        
        // Create a date for the first day of the current month
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        // Create a date for the last day of the current month
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
        
        // Check if any part of the event falls within the current month
        if (
          (startDate <= lastDayOfMonth && endDate >= firstDayOfMonth) ||
          (startDate.getMonth() < currentMonth && endDate.getMonth() > currentMonth && 
           startDate.getFullYear() <= currentYear && endDate.getFullYear() >= currentYear)
        ) {
          isInCurrentMonth = true;
        }
      }
      
      // If the event is in the current month, count it by location
      if (isInCurrentMonth) {
        if (event.location) {
          const location = event.location.toLowerCase();
          if (location.includes('castle')) {
            eventsByLocation.castle++;
          } else if (location.includes('parkland')) {
            eventsByLocation.parkland++;
          } else if (location.includes('garden')) {
            eventsByLocation.garden++;
          } else if (location.includes('retail') || location.includes('shop')) {
            eventsByLocation.retail++;
          } else {
            eventsByLocation.other++;
          }
        } else {
          eventsByLocation.other++;
        }
      }
    });
    
    return (
      <CalendarContainer>
        <CalendarHeader>
          <h2>Events Calendar</h2>
          <CalendarControls>
            <button onClick={() => setCurrentDate(new Date())}>Today</button>
            <button onClick={() => {
              const newDate = new Date(currentDate);
              newDate.setDate(1); // Set to 1st to avoid month skipping
              newDate.setMonth(currentDate.getMonth() - 1);
              setCurrentDate(newDate);
            }}>Previous Month</button>
            <button onClick={() => {
              const newDate = new Date(currentDate);
              newDate.setDate(1); // Set to 1st to avoid month skipping
              newDate.setMonth(currentDate.getMonth() + 1);
              setCurrentDate(newDate);
            }}>Next Month</button>
            <button onClick={debugEvents} style={{ marginLeft: '10px', background: '#f39c12' }}>
              Debug Events
            </button>
          </CalendarControls>
        </CalendarHeader>
        
        <DatePicker
          selected={currentDate}
          onChange={date => setCurrentDate(date)}
          inline
          monthsShown={1}
          dayClassName={date => {
            const events = getEventsForDay(date);
            if (events.length > 0) {
              // Determine the color based on the first event's location
              const firstEvent = events[0];
              if (firstEvent.location) {
                const location = firstEvent.location.toLowerCase();
                if (location.includes('castle')) {
                  return 'castle-event-day';
                } else if (location.includes('parkland')) {
                  return 'parkland-event-day';
                } else if (location.includes('garden')) {
                  return 'garden-event-day';
                } else if (location.includes('retail') || location.includes('shop')) {
                  return 'retail-event-day';
                }
              }
              return 'other-event-day';
            }
            return null;
          }}
          renderDayContents={(day, date) => {
            const events = getEventsForDay(date);
            if (events.length === 0) return day;
            
            return <CalendarDay 
              day={day} 
              date={date} 
              events={events} 
              handleEventClick={handleEventClick} 
            />;
          }}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled
          }) => (
            <div style={{ margin: '0 10px', display: 'flex', justifyContent: 'center' }}>
              <CalendarMonthTitle>{format(date, 'MMMM yyyy')}</CalendarMonthTitle>
            </div>
          )}
        />
        
        <CalendarLegend>
          <LegendTitle>Event Types</LegendTitle>
          <LegendItem>
            <LegendColor style={{ backgroundColor: themeColors.castle }} />
            <span>Castle Events ({eventsByLocation.castle})</span>
          </LegendItem>
          <LegendItem>
            <LegendColor style={{ backgroundColor: themeColors.parkland }} />
            <span>Parkland Events ({eventsByLocation.parkland})</span>
          </LegendItem>
          <LegendItem>
            <LegendColor style={{ backgroundColor: themeColors.garden }} />
            <span>Garden Events ({eventsByLocation.garden})</span>
          </LegendItem>
          <LegendItem>
            <LegendColor style={{ backgroundColor: themeColors.retail }} />
            <span>Retail Village Events ({eventsByLocation.retail})</span>
          </LegendItem>
          <LegendItem>
            <LegendColor style={{ backgroundColor: themeColors.other }} />
            <span>Other Events ({eventsByLocation.other})</span>
          </LegendItem>
        </CalendarLegend>
      </CalendarContainer>
    );
  };
  
  // Loading state
  if (eventsLoading && !eventsData) {
    return (
      <EventsContainer>
        <ParallaxBanner 
          welcomeText="What's on"
          title="Events at Belvoir Castle"
          subtitle="DISCOVER WHAT'S HAPPENING THROUGHOUT THE YEAR"
          backgroundImage={EVENTS_BANNER_CDN}
        />
        <EventsIntroSection>
          <EventsIntroInner>
            <EventsIntroEyebrow>Explore</EventsIntroEyebrow>
            <EventsIntroTitle>
              Seasonal moments, tours &amp; ticketed experiences
            </EventsIntroTitle>
            <EventsIntroSubtitle>
              Browse highlights, search and filter by location, or open the calendar to plan by date.
            </EventsIntroSubtitle>
          </EventsIntroInner>
        </EventsIntroSection>
        <ContentContainer>
          <LoadingSpinner />
        </ContentContainer>
        <Footer />
      </EventsContainer>
    );
  }
  
  // Error state - only show error if both queries fail
  if (eventsError && featuredError) {

    return (
      <EventsContainer>
        <ParallaxBanner 
          welcomeText="What's on"
          title="Events at Belvoir Castle"
          subtitle="DISCOVER WHAT'S HAPPENING THROUGHOUT THE YEAR"
          backgroundImage={EVENTS_BANNER_CDN}
        />
        <EventsIntroSection>
          <EventsIntroInner>
            <EventsIntroEyebrow>Explore</EventsIntroEyebrow>
            <EventsIntroTitle>
              Seasonal moments, tours &amp; ticketed experiences
            </EventsIntroTitle>
            <EventsIntroSubtitle>
              Browse highlights, search and filter by location, or open the calendar to plan by date.
            </EventsIntroSubtitle>
          </EventsIntroInner>
        </EventsIntroSection>
        <ContentContainer>
          <NoEventsMessage>
            <h3>Error Loading Events</h3>
            <p>We&apos;re sorry, but there was an error loading the events. Please try again later.</p>
          </NoEventsMessage>
        </ContentContainer>
        <Footer />
      </EventsContainer>
    );
  }
  
  return (
    <EventsContainer>
      <GlobalStyle />
      <ParallaxBanner 
        welcomeText="What's on"
        title="Events at Belvoir Castle"
        subtitle="DISCOVER WHAT'S HAPPENING THROUGHOUT THE YEAR"
        backgroundImage={EVENTS_BANNER_CDN}
      />

      <EventsIntroSection>
        <EventsIntroInner>
          <EventsIntroEyebrow>Explore</EventsIntroEyebrow>
          <EventsIntroTitle>
            Seasonal moments, tours &amp; ticketed experiences
          </EventsIntroTitle>
          <EventsIntroSubtitle>
            Browse highlights, search and filter by location, or open the calendar to plan by date.
          </EventsIntroSubtitle>
        </EventsIntroInner>
      </EventsIntroSection>

      <ContentContainer>
        {christmasEvent && (
          <EventsPageSection>
            <SectionHeader>
              <SectionEyebrowRow>
                <SectionEyebrowLine aria-hidden />
                <SectionEyebrow>Seasonal highlight</SectionEyebrow>
              </SectionEyebrowRow>
              <SectionHeading>Christmas at Belvoir Castle</SectionHeading>
            </SectionHeader>
            <MainFeaturedEvent>
              <FeaturedHeroVisual $image={christmasEvent.image || christmasImage}>
                <FeaturedBadge>
                  <FontAwesomeIcon icon={faStar} />
                  Christmas
                </FeaturedBadge>
              </FeaturedHeroVisual>
              <FeaturedHeroPanel>
                <EventTitle $main={true}>{christmasEvent.title}</EventTitle>
                <FeaturedMetaRow>
                  <FeaturedMetaItem>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {christmasEvent.date}
                  </FeaturedMetaItem>
                  <FeaturedMetaItem>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {christmasEvent.location || 'Belvoir Castle'}
                  </FeaturedMetaItem>
                </FeaturedMetaRow>
                <EventDescription>
                  {christmasEvent.description}
                </EventDescription>
                <EventActions>
                  <EventButton onClick={() => handleEventClick(christmasEvent)}>
                    View Event Details
                    <FontAwesomeIcon icon={faChevronRight} />
                  </EventButton>
                  <AddToCalendarButton onClick={() => shareEvent(christmasEvent)}>
                    <FontAwesomeIcon icon={faCalendarPlus} />
                    Add to Calendar
                  </AddToCalendarButton>
                </EventActions>
              </FeaturedHeroPanel>
            </MainFeaturedEvent>
          </EventsPageSection>
        )}

        <EventsPageSection>
          <SectionHeader>
            <SectionEyebrowRow>
              <SectionEyebrowLine aria-hidden />
              <SectionEyebrow>Highlights</SectionEyebrow>
            </SectionEyebrowRow>
            <SectionHeading>Featured events</SectionHeading>
            <SectionLead>
              Discover highlighted experiences at Belvoir Castle — from seasonal celebrations to special exhibitions and one-off moments throughout the year.
            </SectionLead>
          </SectionHeader>

          {featuredList.length > 0 ? (
            <FeaturedGrid>
              <MainFeaturedEvent>
                <FeaturedHeroVisual $image={featuredList[0].image || eventsImage}>
                  <FeaturedBadge>
                    <FontAwesomeIcon icon={faStar} />
                    Featured
                  </FeaturedBadge>
                </FeaturedHeroVisual>
                <FeaturedHeroPanel>
                  <EventTitle $main={true}>{featuredList[0].title}</EventTitle>
                  {featuredList[0].subtitle && (
                    <EventSubtitle $main={true}>{featuredList[0].subtitle}</EventSubtitle>
                  )}
                  <FeaturedMetaRow>
                    {!featuredList[0].subtitle && (
                      <FeaturedMetaItem>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {featuredList[0].date}
                      </FeaturedMetaItem>
                    )}
                    <FeaturedMetaItem>
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {featuredList[0].location || 'Belvoir Castle'}
                    </FeaturedMetaItem>
                  </FeaturedMetaRow>
                  <EventDescription>
                    {featuredList[0].description}
                  </EventDescription>
                  <EventActions>
                    <EventButton onClick={() => handleEventClick(featuredList[0])}>
                      View Event Details
                      <FontAwesomeIcon icon={faChevronRight} />
                    </EventButton>
                    <AddToCalendarButton onClick={() => shareEvent(featuredList[0])}>
                      <FontAwesomeIcon icon={faCalendarPlus} />
                      Add to Calendar
                    </AddToCalendarButton>
                  </EventActions>
                </FeaturedHeroPanel>
              </MainFeaturedEvent>

              <FeaturedSecondaryRow>
                {featuredList.slice(1, 3).map((event, index) => (
                  <FeaturedEvent key={event.id || index}>
                    <FeaturedCompactVisual $image={event.image || eventsImage}>
                      <FeaturedBadge $compact>
                        <FontAwesomeIcon icon={faStar} />
                        Featured
                      </FeaturedBadge>
                    </FeaturedCompactVisual>
                    <FeaturedCompactPanel>
                      <EventTitle>{event.title}</EventTitle>
                      {event.subtitle && <EventSubtitle>{event.subtitle}</EventSubtitle>}
                      <FeaturedMetaRow>
                        {!event.subtitle && (
                          <FeaturedMetaItem>
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            {event.date}
                          </FeaturedMetaItem>
                        )}
                        <FeaturedMetaItem>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          {event.location || 'Belvoir Castle'}
                        </FeaturedMetaItem>
                      </FeaturedMetaRow>
                      <FeaturedExcerpt>{event.description}</FeaturedExcerpt>
                      <EventActions $pushDown>
                        <EventButton onClick={() => handleEventClick(event)}>
                          View Details
                          <FontAwesomeIcon icon={faChevronRight} />
                        </EventButton>
                        <AddToCalendarButton onClick={() => shareEvent(event)}>
                          <FontAwesomeIcon icon={faCalendarPlus} />
                          Add to Calendar
                        </AddToCalendarButton>
                      </EventActions>
                    </FeaturedCompactPanel>
                  </FeaturedEvent>
                ))}
              </FeaturedSecondaryRow>
            </FeaturedGrid>
          ) : (
            <NoEventsMessage>
              <h3>No Featured Events</h3>
              <p>Check back soon for upcoming featured events at Belvoir Castle.</p>
            </NoEventsMessage>
          )}
        </EventsPageSection>
        
        <EventsPageSection>
          <SectionHeader>
            <SectionEyebrowRow>
              <SectionEyebrowLine aria-hidden />
              <SectionEyebrow>Full listing</SectionEyebrow>
            </SectionEyebrowRow>
            <SectionHeading>Upcoming events</SectionHeading>
            <SectionLead>
              Search by name, filter by location, or switch to the calendar to see what&apos;s on by date.
            </SectionLead>
          </SectionHeader>

          <FilterSection>
            <SearchBar>
              <FontAwesomeIcon icon={faSearch} />
              <input 
                type="text" 
                placeholder="Search events..." 
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </SearchBar>
            <FilterToolsRow>
              <FilterLabel>
                <FontAwesomeIcon icon={faFilter} />
                Location
              </FilterLabel>
              <FilterDropdown value={filter} onChange={handleFilterChange}>
                <option value="all">All Locations</option>
                <option value="castle">Castle</option>
                <option value="parkland">Parkland</option>
                <option value="gardens">Gardens</option>
                <option value="retail village">Retail Village</option>
              </FilterDropdown>
              <ViewToggleContainer>
                <ViewToggleButton 
                  $active={viewMode === 'list'} 
                  onClick={() => setViewMode('list')}
                >
                  <FontAwesomeIcon icon={faListUl} />
                  List
                </ViewToggleButton>
                <ViewToggleButton 
                  $active={viewMode === 'calendar'} 
                  onClick={() => setViewMode('calendar')}
                >
                  <FontAwesomeIcon icon={faCalendarDay} />
                  Calendar
                </ViewToggleButton>
              </ViewToggleContainer>
            </FilterToolsRow>
          </FilterSection>
          
          {viewMode === 'calendar' ? (
            renderCalendarView()
          ) : displayedEvents.length > 0 ? (
            <>
              <EventsGrid>
                {displayedEvents.slice(0, visibleCount).map((event, index) => {
                  const isPirate = event.isPirateTakeover || isPirateTakeoverByTitle(event.title);
                  return (
                  <EventCard key={index} $isPirateTakeover={isPirate}>
                    <CardImage $image={event.image || eventsImage} />
                    <CardContent>
                      {isPirate && (
                        <PirateTakeoverBadge>
                          <span aria-hidden="true">&#9875;</span>
                          Pirate Takeover
                        </PirateTakeoverBadge>
                      )}
                      <CardTitle>{event.title}</CardTitle>
                      {event.subtitle && (
                        <CardSubtitle>{event.subtitle}</CardSubtitle>
                      )}
                      {!event.subtitle && (
                        <CardInfo>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          {event.date}
                        </CardInfo>
                      )}
                      <CardInfo>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        {event.location || 'Belvoir Castle'}
                      </CardInfo>
                      {event.ticketTier && (
                        <CardInfo>
                          <FontAwesomeIcon icon={faTicketAlt} />
                          {event.ticketTier}
                        </CardInfo>
                      )}
                      <CardDescription>
                        {event.description}
                      </CardDescription>
                      <CardActions>
                        <CardButton onClick={() => handleEventClick(event)}>
                          View Details
                          <FontAwesomeIcon icon={faChevronRight} />
                        </CardButton>
                        <CardButton onClick={() => shareEvent(event)} aria-label="Add to calendar">
                          <FontAwesomeIcon icon={faCalendarPlus} />
                        </CardButton>
                      </CardActions>
                    </CardContent>
                  </EventCard>
                  );
                })}
              </EventsGrid>
              
              {visibleCount < displayedEvents.length && (
                <LoadMoreButton onClick={handleLoadMore}>
                  Load More Events
                </LoadMoreButton>
              )}
            </>
          ) : (
            <NoEventsMessage>
              <h3>No Events Found</h3>
              <p>
                {searchTerm || filter !== 'all' 
                  ? 'No events match your search criteria. Try adjusting your filters.' 
                  : 'Check back soon for upcoming events at Belvoir Castle.'}
              </p>
            </NoEventsMessage>
          )}
        </EventsPageSection>
      </ContentContainer>
      
      <Footer />
    </EventsContainer>
  );
};

export default Events; 